import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export async function getCurrentNda() {
  const { data } = await api.get("/nda/current");
  return data;
}

export async function legalIndex() {
  const { data } = await api.get("/legal/documents");
  return data.documents;
}

export async function legalDoc(docType) {
  const { data } = await api.get(`/legal/documents/${docType}`);
  return data;
}

export async function submitLead(payload) {
  const { data } = await api.post("/leads/submit", payload);
  return data;
}

// --- Insights (Mongo-backed content registry) ---
export async function listInsights(category) {
  const { data } = await api.get("/insights", { params: category ? { category } : {} });
  return data.articles;
}
export function insightView(slug) {
  api.post(`/insights/${slug}/view`).catch(() => {});
}

// --- Marketing / Growth (admin) ---
export async function adminMarketingOverview() {
  const { data } = await api.get("/internal/marketing/overview", { headers: adminHeaders() });
  return data;
}
export async function adminMarketingCampaigns() {
  const { data } = await api.get("/internal/marketing/campaigns", { headers: adminHeaders() });
  return data;
}
export async function adminUpdateCampaignStatus(id, status) {
  const { data } = await api.patch(`/internal/marketing/campaigns/${id}`, { status }, { headers: adminHeaders() });
  return data;
}
export async function adminGetMarketingSettings() {
  const { data } = await api.get("/internal/marketing/settings", { headers: adminHeaders() });
  return data;
}
export async function adminUpdateMarketingSettings(payload) {
  const { data } = await api.patch("/internal/marketing/settings", payload, { headers: adminHeaders() });
  return data;
}

export async function assessmentAccess(token, email) {
  const { data } = await api.post("/assessment/access", { token, email });
  return data;
}

export async function assessmentSave(token, email, responses, clarifications) {
  const { data } = await api.patch("/assessment/save", { token, email, responses, clarifications });
  return data;
}

export async function assessmentQuestions(token, email) {
  const { data } = await api.post("/assessment/questions", { token, email });
  return data;
}

export async function reviewAccess(token, email) {
  const { data } = await api.post("/review/access", { token, email });
  return data;
}

export async function assessmentComplete(token, email, responses, clarifications) {
  const { data } = await api.post("/assessment/complete", { token, email, responses, clarifications });
  return data;
}

// --- Internal admin (Kori) ---
const ADMIN_KEY = "airo_admin_pass";
export function setAdminPass(p) { sessionStorage.setItem(ADMIN_KEY, p); }
export function getAdminPass() { return sessionStorage.getItem(ADMIN_KEY) || ""; }
export function clearAdminPass() { sessionStorage.removeItem(ADMIN_KEY); }
const adminHeaders = () => ({ Authorization: `Bearer ${getAdminPass()}` });

export async function adminVerify(passcode) {
  const { data } = await api.post("/internal/auth/verify", { passcode });
  return data;
}
export async function adminListReviews(includeTest = false) {
  const { data } = await api.get("/internal/reviews", { headers: adminHeaders(), params: includeTest ? { include_test: true } : {} });
  return data;
}
export async function adminGetReview(aid) {
  const { data } = await api.get(`/internal/reviews/${aid}`, { headers: adminHeaders() });
  return data;
}
export async function adminSaveReview(aid, review, recommended_solution_key) {
  const { data } = await api.patch(`/internal/reviews/${aid}`, { review, recommended_solution_key }, { headers: adminHeaders() });
  return data;
}
export async function adminPublishReview(aid, autoSend = false) {
  const { data } = await api.post(`/review/publish`, { assessment_id: aid, edits: {}, auto_send: autoSend }, { headers: adminHeaders() });
  return data;
}
export async function adminSendCustomerReview(aid) {
  const { data } = await api.post(`/internal/reviews/${aid}/send-customer-review`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminRegenerateReview(aid) {
  const { data } = await api.post(`/internal/reviews/${aid}/regenerate`, {}, { headers: adminHeaders() });
  return data;
}
// --- Durable async Product Review generation jobs ---
export async function adminGenerateReview(aid) {
  const { data } = await api.post(`/internal/reviews/${aid}/generate`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminGetJob(jobId) {
  const { data } = await api.get(`/internal/generation-jobs/${jobId}`, { headers: adminHeaders() });
  return data;
}
export async function adminActiveJobs() {
  const { data } = await api.get(`/internal/generation-jobs/active`, { headers: adminHeaders() });
  return data.jobs;
}
export async function adminReviewJob(aid) {
  const { data } = await api.get(`/internal/reviews/${aid}/job`, { headers: adminHeaders() });
  return data;
}
export async function adminRetryOption(jobId, n) {
  const { data } = await api.post(`/internal/generation-jobs/${jobId}/retry-option/${n}`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminFailedReviews() {
  const { data } = await api.get(`/internal/failed-reviews`, { headers: adminHeaders() });
  return data.failed;
}

// --- Client Workspace (Phase 5) ---
export async function commercialSelect(token, email, solution_key) {
  const { data } = await api.post("/commercial/select", { token, email, solution_key });
  return data;
}
export async function workspaceAccess(token, email) {
  const { data } = await api.post("/workspace/access", { token, email });
  return data;
}
export async function workspaceDocuments(token, email) {
  const { data } = await api.post("/workspace/documents", { token, email });
  return data;
}
export async function workspaceDownloadDocument(token, email, documentId, fmt) {
  const res = await api.post(`/workspace/documents/${documentId}/download`, { token, email, fmt }, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
}
export async function proposalAccept(token, email) {
  const { data } = await api.post("/workspace/proposal/accept", { token, email });
  return data;
}
export async function proposalRequestChanges(token, email, message) {
  const { data } = await api.post("/workspace/proposal/request-changes", { token, email, message });
  return data;
}
export async function agreementAccept(token, email, typed_signature) {
  const { data } = await api.post("/workspace/agreement/accept", { token, email, typed_signature });
  return data;
}
export async function paymentCheckout(token, email, origin_url) {
  const { data } = await api.post("/workspace/payment/checkout", { token, email, origin_url });
  return data;
}
export async function workspaceDepositSummary(token, email) {
  const { data } = await api.post("/workspace/deposit-summary", { token, email });
  return data;
}
export async function workspaceDepositCheckout(token, email, origin_url) {
  const { data } = await api.post("/workspace/deposit/checkout", { token, email, origin_url });
  return data;
}
export async function adminReleaseDeposit(proposalId) {
  const { data } = await api.post(`/internal/proposals/${proposalId}/release-deposit`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminDepositView(proposalId) {
  const { data } = await api.get(`/internal/proposals/${proposalId}/deposit`, { headers: adminHeaders() });
  return data;
}
export async function adminRequestMilestone(projectId, n) {
  const { data } = await api.post(`/internal/projects/${projectId}/milestones/${n}/request`, {}, { headers: adminHeaders() });
  return data;
}
export async function paymentStatus(session_id) {
  const { data } = await api.get(`/payments/status/${session_id}`);
  return data;
}

// --- Admin: Commercial proposals ---
export async function adminListProposals() {
  const { data } = await api.get("/internal/proposals", { headers: adminHeaders() });
  return data;
}
export async function adminGetProposal(id) {
  const { data } = await api.get(`/internal/proposals/${id}`, { headers: adminHeaders() });
  return data;
}
export async function adminCreateProposal(assessment_id, solution_key) {
  const { data } = await api.post("/internal/proposals", { assessment_id, solution_key }, { headers: adminHeaders() });
  return data;
}
export async function adminSaveProposal(id, patch) {
  const { data } = await api.patch(`/internal/proposals/${id}`, patch, { headers: adminHeaders() });
  return data;
}
export async function adminProposalAction(id, action) {
  const { data } = await api.post(`/internal/proposals/${id}/${action}`, {}, { headers: adminHeaders() });
  return data;
}

// --- Legal Assembly Engine (Phase 3/4) + Handoff (Phase 5) ---
export async function adminListAssembled(proposalId) {
  const { data } = await api.get("/internal/assembly", { headers: adminHeaders(), params: proposalId ? { proposal_id: proposalId } : {} });
  return data;
}
export async function adminGenerateAssembled(proposal_id, document_type) {
  const { data } = await api.post("/internal/assembly/generate", { proposal_id, document_type }, { headers: adminHeaders() });
  return data;
}
export async function adminGetAssembled(id) {
  const { data } = await api.get(`/internal/assembly/${id}`, { headers: adminHeaders() });
  return data;
}
export async function adminFillAssembled(id, filled_fields) {
  const { data } = await api.patch(`/internal/assembly/${id}`, { filled_fields }, { headers: adminHeaders() });
  return data;
}
export async function adminExecuteAssembled(id) {
  const { data } = await api.post(`/internal/assembly/${id}/execute`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminAssembledPdfUrl(id) {
  const res = await api.get(`/internal/assembly/${id}/pdf`, { headers: adminHeaders(), responseType: "blob" });
  return URL.createObjectURL(res.data);
}
export async function adminHandoffStatus(projectId) {
  const { data } = await api.get(`/internal/handoff/${projectId}`, { headers: adminHeaders() });
  return data;
}
export async function adminGenerateHandoff(projectId) {
  const { data } = await api.post(`/internal/handoff/${projectId}/generate`, {}, { headers: adminHeaders() });
  return data;
}

// --- Phase 6: Client project ---
export async function deliverableApprove(token, email, id) {
  const { data } = await api.post("/workspace/deliverable/approve", { token, email, id });
  return data;
}
export async function deliverableRevise(token, email, id, message) {
  const { data } = await api.post("/workspace/deliverable/request-revision", { token, email, id, message });
  return data;
}
export async function workspaceMessage(token, email, message) {
  const { data } = await api.post("/workspace/message", { token, email, message });
  return data;
}
export async function projectPaymentCheckout(token, email, payment_id, origin_url) {
  const { data } = await api.post("/workspace/project/payment/checkout", { token, email, payment_id, origin_url });
  return data;
}

// --- Phase 6: Admin projects ---
export async function adminListProjects() {
  const { data } = await api.get("/internal/projects", { headers: adminHeaders() });
  return data;
}
export async function adminGetProject(id) {
  const { data } = await api.get(`/internal/projects/${id}`, { headers: adminHeaders() });
  return data;
}
export async function adminSaveProject(id, patch) {
  const { data } = await api.patch(`/internal/projects/${id}`, patch, { headers: adminHeaders() });
  return data;
}
export async function adminCreateMilestone(id, body) {
  const { data } = await api.post(`/internal/projects/${id}/milestones`, body, { headers: adminHeaders() });
  return data;
}
export async function adminSaveMilestone(id, patch) {
  const { data } = await api.patch(`/internal/milestones/${id}`, patch, { headers: adminHeaders() });
  return data;
}
export async function adminCreateDeliverable(mid, body) {
  const { data } = await api.post(`/internal/milestones/${mid}/deliverables`, body, { headers: adminHeaders() });
  return data;
}
export async function adminSaveDeliverable(id, patch) {
  const { data } = await api.patch(`/internal/deliverables/${id}`, patch, { headers: adminHeaders() });
  return data;
}
export async function adminProjectMessage(id, name) {
  const { data } = await api.post(`/internal/projects/${id}/message`, { name }, { headers: adminHeaders() });
  return data;
}


// --- Phase 2: Legal Center + Agreement Packages (admin) ---
export async function adminLegalCenter() {
  const { data } = await api.get("/internal/legal/center", { headers: adminHeaders() });
  return data.documents;
}
export async function adminListPackages() {
  const { data } = await api.get("/internal/packages", { headers: adminHeaders() });
  return data.packages;
}
export async function adminGetPackage(id) {
  const { data } = await api.get(`/internal/packages/${id}`, { headers: adminHeaders() });
  return data;
}
export async function adminCreatePackage(payload) {
  const { data } = await api.post("/internal/packages", payload, { headers: adminHeaders() });
  return data;
}
export async function adminPresentPackage(id) {
  const { data } = await api.post(`/internal/packages/${id}/present`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminInvalidatePackage(id) {
  const { data } = await api.post(`/internal/packages/${id}/invalidate`, {}, { headers: adminHeaders() });
  return data;
}

// --- Phase 2: customer Review & Accept ---
export async function packageAccess(token, email) {
  const { data } = await api.post("/packages/access", { token, email });
  return data;
}
export async function packageAccept(token, email, accepting_user_name) {
  const { data } = await api.post("/packages/accept", { token, email, acknowledged: true, accepting_user_name });
  return data;
}
export async function packageReject(token, email, reason) {
  const { data } = await api.post("/packages/reject", { token, email, reason });
  return data;
}

// --- Phase 3: Deal closing (admin) ---
export async function adminListDeals() {
  const { data } = await api.get("/internal/deals", { headers: adminHeaders() });
  return data.deals;
}
export async function adminGetDeal(id) {
  const { data } = await api.get(`/internal/deals/${id}`, { headers: adminHeaders() });
  return data;
}
export async function adminSaveDeal(payload) {
  const { data } = await api.post("/internal/deals", payload, { headers: adminHeaders() });
  return data;
}
export async function adminPreviewDeal(id) {
  const { data } = await api.post(`/internal/deals/${id}/preview`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminCreateAgreementPayment(id) {
  const { data } = await api.post(`/internal/deals/${id}/create-agreement-payment`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminCloseDeal(proposalId) {
  const { data } = await api.post(`/internal/proposals/${proposalId}/close-deal`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminDpaOverride(dealId, decision, reason) {
  const { data } = await api.post(`/internal/deals/${dealId}/dpa-override`, { decision, reason }, { headers: adminHeaders() });
  return data;
}

// --- Admin settings + notification center ---
export async function adminGetSettings() {
  const { data } = await api.get("/internal/settings", { headers: adminHeaders() });
  return data;
}
export async function adminChangePasscode(current, next) {
  const { data } = await api.post("/internal/settings/change-passcode", { current, new: next }, { headers: adminHeaders() });
  return data;
}
export async function adminUpdateNotifications(notifications, primary_alert_email) {
  const { data } = await api.patch("/internal/settings/notifications", { notifications, primary_alert_email }, { headers: adminHeaders() });
  return data;
}
export async function adminListAlerts(category) {
  const { data } = await api.get("/internal/alerts", { headers: adminHeaders(), params: category ? { category } : {} });
  return data;
}

// --- Services & Offerings Catalog ---
export async function publicCatalog() {
  const { data } = await api.get("/catalog/services");
  return data;
}
export async function adminListCatalog() {
  const { data } = await api.get("/internal/catalog/services", { headers: adminHeaders() });
  return data;
}
export async function adminSetLineItems(proposalId, payload) {
  const { data } = await api.patch(`/internal/proposals/${proposalId}/line-items`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminCreateService(payload) {
  const { data } = await api.post("/internal/catalog/services", payload, { headers: adminHeaders() });
  return data;
}
export async function adminPatchService(serviceId, payload) {
  const { data } = await api.patch(`/internal/catalog/services/${serviceId}`, payload, { headers: adminHeaders() });
  return data;
}
export async function adminToggleService(serviceId) {
  const { data } = await api.post(`/internal/catalog/services/${serviceId}/toggle`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminCatalogQuote(payload) {
  const { data } = await api.post("/internal/catalog/quote", payload, { headers: adminHeaders() });
  return data;
}
export async function adminDetectUpsells(text, use_llm = true) {
  const { data } = await api.post("/internal/catalog/detect-upsells", { text, use_llm }, { headers: adminHeaders() });
  return data;
}
// --- Phase B document pipeline ---
export async function adminGenerateDoc(payload) {
  const { data } = await api.post("/internal/documents/generate", payload, { headers: adminHeaders() });
  return data;
}
export async function adminApproveDoc(id) {
  const { data } = await api.post(`/internal/documents/${id}/approve`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminSendDoc(id) {
  const { data } = await api.post(`/internal/documents/${id}/send`, {}, { headers: adminHeaders() });
  return data;
}
export async function adminDownloadDoc(id, fmt) {
  const res = await api.get(`/internal/documents/${id}/${fmt}`, { headers: adminHeaders(), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
}
export async function adminDownloadReviewPdf(aid) {
  const res = await api.get(`/internal/reviews/${aid}/pdf`, { headers: adminHeaders(), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
}

// --- Phase 3: customer closing ---
export async function closingAccess(token, email) {
  const { data } = await api.post("/closing/access", { token, email });
  return data;
}
export async function closingSign(token, email, document_type, typed_signature) {
  const { data } = await api.post("/closing/sign", { token, email, document_type, typed_signature });
  return data;
}
export async function closingPay(token, email, origin_url) {
  const { data } = await api.post("/closing/pay", { token, email, origin_url });
  return data;
}
